import React from "react";
import PropTypes from "prop-types";
import { cn } from "../cn";
import styles from "./Skeleton.module.scss";

/**
 * Skeleton — placeholder block for not-yet-loaded content.
 *
 * Reserve the *exact* geometry of the content it stands in for so the layout
 * does not shift when real content replaces it. Build the skeleton from the
 * same sizing tokens as the real component so the two can't drift apart.
 *
 *   <Skeleton width="100%" height={20} />           // a line
 *   <Skeleton shape="line" />                        // one text line
 *   <Skeleton shape="circle" width={40} height={40} />
 *
 * Shimmer runs off `--cb-duration-shimmer`; under prefers-reduced-motion the
 * token collapses and the animation is disabled (see Skeleton.module.scss).
 */
export function Skeleton({
  shape = "rect",
  width,
  height,
  radius,
  className,
  style,
  ...rest
}) {
  const inlineStyle = {
    ...(width != null ? { width } : null),
    ...(height != null ? { height } : null),
    ...(radius != null ? { borderRadius: radius } : null),
    ...style,
  };

  return (
    <span
      aria-hidden="true"
      className={cn(styles.skeleton, styles[shape], className)}
      style={inlineStyle}
      {...rest}
    />
  );
}

Skeleton.propTypes = {
  /** Visual shape — controls default sizing/radius. */
  shape: PropTypes.oneOf(["rect", "line", "circle"]),
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  radius: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  className: PropTypes.string,
  style: PropTypes.object,
};

export default Skeleton;
